import type { PainPoint, Cut } from '@/types/quiz'

/**
 * Pain-point → "why this match" bullet mapping.
 *
 * Used on the result screen's 3-bullet list. We take the user's selected pain
 * points, pick up to 3, and look up each one's matching feature bullet.
 *
 * Voice rules: AUS/UK English, en dashes with spaces, "our [Product]" never
 * "the [Product]", no "we've all been there" / "let's be honest" / "real
 * women" framing.
 */
export const PAIN_BULLETS: Record<PainPoint, string> = {
  waist_gap:     "Four-point internal waist adjustment cinches you in where it's needed most. No more gap between you and your pants.",
  tight_thighs:  'Made for strong legs with 4-way stretch fabric. Designed to give your thighs and bum the room they need, without the squeeze.',
  sized_up_hips: "Built for fuller hips and a smaller waist - now you don't have to size up just to get past your hips.",
  crotch_rips:   'Gusseted crotch and reinforced seams - made to move with you, not split on you.',
  waist_digs:    'Contouring heat-activated stretch panels through the waist, holding you in without digging in.',
  buttons_pop:   "Zips, hooks, loops and clips, plus stretch through the mid-section. Allowing you to bend, sit, and fall without worrying about that dreaded 'pop'.",
  baggy_legs:    'Contoured leg shape that is designed to follow your line - no need to sacrifice a good fit on the legs for a good fit anywhere else.',
  length_wrong:  'Three inseam lengths - Short, Regular, Tall - each designed purposely with rises and proportions adjusted for the height.',
  wears_mens:    "Designed around women's proportions from scratch - not a men's pant that's been \"pinked and shrinked\".",
  none:          'Built around bodies first without sacrificing on performance - 20,000mm waterproofing, 4-way stretch, zoned insulation, and lots of pockets.',
}

/**
 * Generic cut-level bullets. Used to backfill when the user selected fewer
 * than 3 pain points, or when their selections only produced universal
 * pains like length_wrong / wears_mens / none.
 */
export const CUT_GENERIC_BULLETS: Record<Cut, readonly string[]> = {
  zali: [
    'A 4-point waist adjustment with a shape-specific design that lets you dial in the waist without having to sacrifice room for your hips and thighs.',
    'Gusseted crotch and reinforced seams - Zali is the answer to the all too familiar crotch-rip fear.',
    'Available in Short, Regular, and Tall - purposely designed with the rise and proportion adjusted for each, not just the hem.',
  ],
  mila: [
    'Heat-activated contouring side stretch panels and a 4-point waist adjustment system for a clean silhouette without the need to sacrifice on comfort.',
    'Designed with a gentle waist-to-hip curve, holding its shape and moving with you.',
    'Available in Short, Regular, and Tall with proportions adjusted for each.',
  ],
  beatrice: [
    'Heat-activated stretch panels and a 2-point waist adjustment, adding room for movement and removing the baggy leg.',
    'Built to sit comfortably where you want it, and designed to stay there - no digging and no popping buttons.',
    'Available in Short and Regular - proportioned for each height, not just hemmed differently.',
  ],
}

/**
 * Pick up to 3 bullets for a given result. Prefers the user's actual pains
 * (in the order they appear in the selection), then backfills with generic
 * cut bullets to reach 3. De-duplicates across sources.
 */
export function pickBullets(painPoints: readonly PainPoint[], cut: Cut, max = 3): string[] {
  const chosen: string[] = []
  const seen = new Set<string>()

  const push = (s: string) => {
    if (chosen.length >= max) return
    if (seen.has(s)) return
    seen.add(s)
    chosen.push(s)
  }

  for (const p of painPoints) push(PAIN_BULLETS[p])
  for (const generic of CUT_GENERIC_BULLETS[cut]) push(generic)

  return chosen
}
